import { useEffect, useRef, useState } from "react";
import { Camera, Trash2, KeyRound, User as UserIcon, AtSign } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { apiError, avatarUrl } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

export default function Account() {
  const { user, refreshMe } = useAuth();
  const [profileForm, setProfileForm] = useState({ first_name: "", last_name: "", email: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarBust, setAvatarBust] = useState(Date.now());
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) setProfileForm({
      first_name: user.first_name || (user.name ? user.name.split(" ")[0] : ""),
      last_name: user.last_name || (user.name ? user.name.split(" ").slice(1).join(" ") : ""),
      email: user.email || "",
    });
  }, [user]);

  if (!user) return null;

  const saveProfile = async (e) => {
    e.preventDefault();
    const fn = profileForm.first_name.trim();
    const ln = profileForm.last_name.trim();
    if (!fn || !ln) return toast.error("First name and last name are required");
    const payload = {};
    if (fn !== (user.first_name || "")) payload.first_name = fn;
    if (ln !== (user.last_name || "")) payload.last_name = ln;
    if (profileForm.email !== user.email) payload.email = profileForm.email;
    if (Object.keys(payload).length === 0) return toast.info("No changes to save");
    setSavingProfile(true);
    try {
      await api.put("/auth/me", payload);
      await refreshMe();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.current_password || !pwForm.new_password) return toast.error("Fill both password fields");
    setSavingPw(true);
    try {
      await api.put("/auth/me", pwForm);
      toast.success("Password updated");
      setPwForm({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingPw(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/auth/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshMe();
      setAvatarBust(Date.now());
      toast.success("Profile picture updated");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const removeAvatar = async () => {
    setUploadingAvatar(true);
    try {
      await api.delete("/auth/me/avatar");
      await refreshMe();
      setAvatarBust(Date.now());
      toast.success("Profile picture removed");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const initial = ((user.first_name || user.name || user.email || "?")[0] || "?").toUpperCase();
  const avatarSrc = user.avatar_path ? avatarUrl(user.id, avatarBust) : null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      <main className="max-w-3xl w-full mx-auto px-6 py-10 flex-1">
        <div className="fade-up">
          <h1 className="font-display font-black text-4xl sm:text-5xl text-[#000000] tracking-tight">My Account</h1>
          <p className="text-gray-500 mt-2 text-sm">Update your profile, change your password, and manage your access.</p>
        </div>

        <section className="mt-10 bg-white border border-gray-200 rounded-2xl p-6 flex flex-col sm:flex-row items-start gap-6" data-testid="avatar-section">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 flex items-center justify-center">
              {avatarSrc ? (
                <img data-testid="account-avatar" src={avatarSrc} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="font-display font-black text-3xl text-[#4A0A66]" data-testid="account-avatar-fallback">{initial}</span>
              )}
            </div>
            <button
              data-testid="upload-avatar-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploadingAvatar}
              aria-label="Upload profile picture"
              className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#DF1283] text-white flex items-center justify-center shadow-lg hover:bg-[#000000] transition-colors disabled:opacity-60"
            >
              <Camera size={15} />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              data-testid="avatar-file-input"
              className="hidden"
              onChange={(e) => { uploadAvatar(e.target.files?.[0]); e.target.value = ""; }}
            />
          </div>
          <div className="flex-1">
            <div className="font-display font-black text-lg text-[#000000]">Profile Picture</div>
            <p className="text-xs text-gray-500 mt-1">Square images look best. We auto-crop and resize to 256×256.</p>
            {user.avatar_path && (
              <button
                data-testid="remove-avatar-btn"
                onClick={removeAvatar}
                disabled={uploadingAvatar}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-xs font-bold text-[#000000] hover:bg-[#DF1283] hover:text-white transition-colors"
              >
                <Trash2 size={12} /> Remove
              </button>
            )}
          </div>
        </section>

        <section className="mt-6 bg-white border border-gray-200 rounded-2xl p-6" data-testid="access-section">
          <h2 className="font-display font-black text-lg text-[#000000]">Access</h2>
          <div className="flex flex-wrap gap-2 mt-3 text-xs text-gray-500">
            <span className={`px-3 py-1 rounded-full font-bold uppercase tracking-[0.12em] text-[11px] text-white ${
              user.role === "admin" ? "bg-[#000000]" :
              !user.access_enabled ? "bg-[#DF1283]" :
              (typeof user.days_remaining === "number" && user.days_remaining <= 3) ? "bg-[#DF1283]" :
              "bg-[#FC861E]"
            }`}>
              {user.role === "admin" ? "Admin" :
                !user.access_enabled ? "Access Disabled" :
                typeof user.days_remaining === "number"
                  ? (user.days_remaining <= 0 ? "Access Expired" : `Access — ${user.days_remaining === 1 ? "1 day left" : `${user.days_remaining} days left`}`)
                  : "Access Active"}
            </span>
            {user.expires_at ? (
              <span className="px-2.5 py-1 rounded-full bg-gray-100">
                Access until <strong className="text-[#000000] ml-1">{formatDate(user.expires_at)}</strong>
                {typeof user.days_remaining === "number" && user.days_remaining <= 7 && (
                  <span className={`ml-1 ${user.days_remaining <= 3 ? "text-[#DF1283]" : "text-[#FC861E]"}`}>
                    ({user.days_remaining <= 0 ? "expired" : `${user.days_remaining} days left`})
                  </span>
                )}
              </span>
            ) : (
              <span className="px-2.5 py-1 rounded-full bg-gray-100">No expiration</span>
            )}
            {user.created_at && (
              <span className="px-2.5 py-1 rounded-full bg-gray-100">Joined {formatDate(user.created_at)}</span>
            )}
          </div>
        </section>

        <section className="mt-6 bg-white border border-gray-200 rounded-2xl p-6" data-testid="profile-section">
          <h2 className="font-display font-black text-lg text-[#000000] flex items-center gap-2">
            <UserIcon size={17} /> Profile Details
          </h2>
          <form onSubmit={saveProfile} className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">First name</label>
                <input
                  data-testid="account-first-name-input"
                  required
                  value={profileForm.first_name}
                  onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Last name</label>
                <input
                  data-testid="account-last-name-input"
                  required
                  value={profileForm.last_name}
                  onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Email</label>
              <div className="relative mt-1.5">
                <AtSign size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  data-testid="account-email-input"
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                />
              </div>
            </div>
            <button
              data-testid="save-profile-btn"
              type="submit"
              disabled={savingProfile}
              className="bg-[#000000] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#4A0A66] transition-colors disabled:opacity-60"
            >
              {savingProfile ? "Saving..." : "Save profile"}
            </button>
          </form>
        </section>

        <section className="mt-6 bg-white border border-gray-200 rounded-2xl p-6" data-testid="password-section">
          <h2 className="font-display font-black text-lg text-[#000000] flex items-center gap-2">
            <KeyRound size={17} /> Change Password
          </h2>
          <form onSubmit={savePassword} className="mt-4 space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Current password</label>
              <input
                data-testid="account-current-password-input"
                type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">New password</label>
              <input
                data-testid="account-new-password-input"
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                placeholder="At least 8 characters"
                className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
              />
            </div>
            <button
              data-testid="save-password-btn"
              type="submit"
              disabled={savingPw}
              className="bg-[#DF1283] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#000000] transition-colors disabled:opacity-60"
            >
              {savingPw ? "Updating..." : "Update password"}
            </button>
          </form>
        </section>
      </main>
      <Footer />
    </div>
  );
}
